import {
  adminGigsterProfilesOnboardingStepOverridesCreate,
  adminGigsterProfilesOnboardingStepOverridesDestroy,
  type GigsterProfileReadonly,
  type OnboardingStepStatus,
  getGigsterProfilesOnboardingStepsListQueryOptions,
  getGigsterProfilesRetrieveQueryOptions,
  getGigsterProfilesWorkHistoryRecordsListQueryOptions,
  getProPreferencesAvailabilityListQueryOptions,
  getCertificationsListQueryOptions,
  getProRetrieveQueryOptions,
  getTimeBlocksListQueryOptions,
} from "@sidegigllc/api/client";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import type { SelectOption } from "types/utils";

type QueryOpts<TData> = Omit<UseQueryOptions<TData, unknown, TData>, "queryKey" | "queryFn">;

export const proProfileQueryOptions = (id: number | null | undefined, options?: QueryOpts<GigsterProfileReadonly>) =>
  getGigsterProfilesRetrieveQueryOptions(id ?? -1, { query: { enabled: id != null, ...options } });

export const useProProfile = (id: number | null | undefined, options?: QueryOpts<GigsterProfileReadonly>) =>
  useQuery(proProfileQueryOptions(id, options));

export const proProfileByUuidQueryOptions = (uuid: string | null | undefined) =>
  getProRetrieveQueryOptions(uuid ?? "", { query: { enabled: !!uuid } });

/** Per-day availability slices. `?pro=` is honoured for gigpro admins only. */
export const proAvailabilityQueryOptions = (id: number | null | undefined) =>
  getProPreferencesAvailabilityListQueryOptions({ pro: id ?? undefined }, { query: { enabled: id != null } });

export const proExperienceSectionQueryOptions = (id: number) =>
  getGigsterProfilesWorkHistoryRecordsListQueryOptions(id);

const onboardingRequirements = ["PROFILE", "STRIPE", "INSURANCE", "INTERVIEW", "W2", "BACKGROUND_CHECK"] as const;
export type OnboardingRequirement = (typeof onboardingRequirements)[number];
export type OnboardingStepProgress =
  | "INCOMPLETE"
  | "ONGOING"
  | "AWAITING_VERIFICATION"
  | "COMPLETED"
  | "FAILED"
  | "EXPIRED";
export type OnboardingStep = { requirement_name: OnboardingRequirement; status: OnboardingStepProgress };

const normalizeStatus = (requirement: OnboardingRequirement, status: string): OnboardingStepProgress => {
  switch (status) {
    case "COMPLETED":
    case "APPROVED":
      return "COMPLETED";
    case "FAILED":
    case "REJECTED":
      return "FAILED";
    case "EXPIRED":
      return "EXPIRED";
    case "NOT_STARTED":
      return requirement === "STRIPE" ? "ONGOING" : "INCOMPLETE";
    case "IN_PROGRESS":
    case "STARTED":
    case "SCHEDULED":
      return "ONGOING";
    // W2: Zeal onboarding done, E-Verify not yet closed authorized.
    case "PENDING_VERIFICATION":
      return "AWAITING_VERIFICATION";
    default:
      return "INCOMPLETE";
  }
};

const isKnownRequirement = (name: string): name is OnboardingRequirement =>
  (onboardingRequirements as ReadonlyArray<string>).includes(name);

const toOnboardingSteps = (steps: Array<OnboardingStepStatus>): Array<OnboardingStep> =>
  steps
    .filter((step) => isKnownRequirement(step.requirement_name))
    .map((step) => ({
      requirement_name: step.requirement_name as OnboardingRequirement,
      status: normalizeStatus(step.requirement_name as OnboardingRequirement, step.status),
    }));

export const proOnboardingStepsQueryOptions = (id: number) =>
  getGigsterProfilesOnboardingStepsListQueryOptions(id, { query: { select: toOnboardingSteps } });

export const setW2Onboarding = (proId: number) =>
  adminGigsterProfilesOnboardingStepOverridesCreate(proId, { requirement: "W2" });
export const removeW2Onboarding = (proId: number) => adminGigsterProfilesOnboardingStepOverridesDestroy(proId, "W2");

type OptionResult = { results: ReadonlyArray<{ id: number; name: string; description?: string | null }> };
const toSelectOptions =
  (preferName: boolean) =>
  (page: OptionResult): Array<SelectOption> =>
    page.results.map(({ id, name, description }) => ({ value: id, label: preferName ? name : (description ?? name) }));

/** Raw blocks with their hours, for naming a merged availability span the API left unlabelled. */
export const timeBlocksQueryOptions = (enabled = true) =>
  getTimeBlocksListQueryOptions(undefined, { query: { enabled, select: (page) => page.results } });
export const certificationsOptionsQueryOptions = () =>
  getCertificationsListQueryOptions(undefined, { query: { select: toSelectOptions(false) } });
export const useCertificationsOptions = () => useQuery(certificationsOptionsQueryOptions());
